/**
 * Interface for CSV parsers
 */
export interface CsvParser {
  parseCSV(csvText: string): any;
}

/**
 * Base abstract class for CSV parsers
 */
export abstract class BaseCsvParser implements CsvParser {
  /**
   * Parse CSV text into an array of objects
   * @param csvText The CSV text to parse
   * @returns An array of objects parsed from the CSV
   */
  parseCSV(csvText: string): any {
    if (!csvText || typeof csvText !== 'string') {
      console.error('Invalid CSV text:', csvText);
      return [];
    }

    const lines = csvText.split('\n');

    if (lines.length === 0 || !lines[0]) {
      console.error('CSV has no lines or first line is empty');
      return [];
    }

    const headers = lines[0].split(',').map((header) => header.trim());

    // Check for required columns
    const requiredColumns = this.getRequiredColumns();
    const missingColumns = requiredColumns.filter(col => !headers.includes(col));

    if (missingColumns.length > 0) {
      console.error(`CSV format missing required columns: ${missingColumns.join(', ')}`, headers);
      return [];
    }

    // Create an array of objects
    const result = [];

    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;

      const values = lines[i].split(',').map((value) => value.trim());

      // Create a record object mapping headers to values
      const record = {};
      headers.forEach((header, index) => {
        if (index < values.length) {
          record[header] = values[index];
        } else {
          record[header] = '';
        }
      });

      // Only add records that have all required fields
      if (this.validateRecord(record)) {
        const recordObject = this.createRecordObject(record, headers);
        result.push(recordObject);
      }
    }

    return result;
  }

  /**
   * Get the list of required columns for this parser
   * @returns Array of required column names
   */
  protected abstract getRequiredColumns(): string[];

  /**
   * Validate if a record has all required fields
   * @param record The record to validate
   * @returns True if the record is valid, false otherwise
   */
  protected abstract validateRecord(record: any): boolean;

  /**
   * Create a record object from the parsed CSV record
   * @param record The parsed CSV record
   * @param headers The CSV headers
   * @returns The record object
   */
  protected abstract createRecordObject(record: any, headers: string[]): any;
}
